import { EnvironmentInjector, Injectable, NgZone, runInInjectionContext, signal } from '@angular/core';
import { Auth, onAuthStateChanged, signOut, type User } from '@angular/fire/auth';
import { BehaviorSubject } from 'rxjs';

export interface AdminAppsUser {
  uid: string;
  email: string | null;
  name: string | null;
  authenticated: boolean;
  role: string;
  roles: string[];
  userType: string;
  businessHandler: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private firebaseUser = signal<User | null>(null);
  private authInitialized = signal(false);

  private currentUserSubject = new BehaviorSubject<AdminAppsUser>({
    uid: '',
    email: null,
    name: null,
    authenticated: false,
    role: '',
    roles: [],
    userType: 'platform',
    businessHandler: 'platform',
  });

  readonly currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private auth: Auth,
    private injector: EnvironmentInjector,
    private ngZone: NgZone
  ) {
    onAuthStateChanged(this.auth, async (user: User | null) => {
      await runInInjectionContext(this.injector, async () => {
        this.ngZone.run(() => {
          this.firebaseUser.set(user);

          if (!user) {
            this.currentUserSubject.next({
              uid: '',
              email: null,
              name: null,
              authenticated: false,
              role: '',
              roles: [],
              userType: 'platform',
              businessHandler: 'platform',
            });
            this.authInitialized.set(true);
            return;
          }

          const role = 'superadmin';
          this.currentUserSubject.next({
            uid: user.uid,
            email: user.email,
            name: user.displayName,
            authenticated: true,
            role,
            roles: [role],
            userType: 'platform',
            businessHandler: 'platform',
          });
          this.authInitialized.set(true);
        });
      });
    });
  }

  isAuthenticated(): boolean {
    return !!this.firebaseUser();
  }

  async waitForAuthInit(): Promise<void> {
    if (this.authInitialized()) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Timeout waiting for auth initialization')), 5000);
      const checkInterval = setInterval(() => {
        if (this.authInitialized()) {
          clearInterval(checkInterval);
          clearTimeout(timeout);
          resolve();
        }
      }, 25);
    });
  }

  async signOut(): Promise<void> {
    await signOut(this.auth);
  }
}
